import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ID } from '@ianesthesie-kit/services';
import { DeskPayment } from './deskPayment.model';
import {
  addToDeskPaymentRegistry,
  deskPaymentChainRootId,
  isLiveDeskPaymentPending,
  refreshDeskPaymentRegistry
} from './deskPayment.utils';

/**
 * The desk payments this browser has taken responsibility for finishing, and the ones a
 * dialog's tracker is already polling. App-wide rather than per surface because the duty to
 * finish a payment outlives whatever started it: the chase drawer is unmounted when it closes,
 * a page navigates, a row leaves its status tab — while `POST /refresh` still has to run until
 * the card is captured or the backend sweeper closes it out. Webhooks are wired since 3c and can
 * advance the very same row, but the browser only learns of that by reading again. Cleared on
 * logout with the rest of the store.
 *
 * Fed by `deskPaymentRegistry.listener.ts` rather than by the surfaces: every desk-payment read
 * of the store reports here, so the registry is current whether or not anything is on screen.
 * A payment enters it either because a surface started or resumed it (`registerDeskPayment`) or
 * because a patient-scoped list read adopted it — so a payment left running when the tab is
 * reloaded, or opened in a second tab or at a second desk, is picked back up on the next read
 * of that patient's list, within the 20-minute pending horizon. The chase dashboard's rows
 * never adopt: they span the hospital, and taking charge of every desk's live payments is the
 * one thing this browser has no business doing.
 */
export type DeskPaymentRegistryState = {
  registry: DeskPayment[];
  /**
   * How many mounted surfaces are following each intent, not merely whether one is: the chase
   * dashboard and the patient drawer opened over it are co-mounted, and both can have a dialog
   * on the same payment — the first to unmount must not clear the follow the other still holds.
   */
  followedPaymentIntentCounts: Record<ID, number>;
};

const initialState: DeskPaymentRegistryState = {
  registry: [],
  followedPaymentIntentCounts: {}
};

const deskPaymentRegistrySlice = createSlice({
  name: 'deskPaymentRegistry',
  initialState,
  reducers: {
    /**
     * Takes responsibility for a payment a surface just started or resumed — what makes a
     * payment this browser's before any read has reported on it. `now` travels in the payload
     * so the reducer stays pure: the horizon the helper applies is a function of time, and
     * reading the clock inside a reducer would make it one too.
     */
    registerDeskPayment: {
      reducer: (
        state,
        { payload }: PayloadAction<{ payment: DeskPayment; now: number }>
      ) => {
        state.registry = addToDeskPaymentRegistry(
          state.registry,
          payload.payment,
          payload.now
        );
      },
      prepare: (payment: DeskPayment) => ({
        payload: { payment, now: Date.now() }
      })
    },
    /**
     * What a read has just seen of the desk payments — a capture taken at another desk, the
     * outcome of a poll, a mutation's own response. Registered entries are updated from it and
     * the finished ones dropped, so every read of every surface feeds the one registry without
     * clobbering the others: only the payments a read mentions are touched.
     *
     * `adopt` is the caller's answer to "is this browser entitled to take these on?". A
     * patient-scoped list read says yes — it is one patient's desk, and adopting is what picks
     * a payment back up after a reload or in a second tab. The hospital-wide dashboard says no:
     * its rows carry other desks' live payments, and adopting them would put this browser in
     * charge of work it never started.
     */
    reconcileDeskPayments: {
      reducer: (
        state,
        {
          payload: { seen, adopt, now }
        }: PayloadAction<{
          seen: (DeskPayment | null | undefined)[];
          adopt: boolean;
          now: number;
        }>
      ) => {
        const refreshed = refreshDeskPaymentRegistry(state.registry, seen, now);
        const registeredChains = new Set(refreshed.map(deskPaymentChainRootId));
        const toAdopt = adopt
          ? seen.filter(
              (payment): payment is DeskPayment =>
                !!payment &&
                !registeredChains.has(deskPaymentChainRootId(payment)) &&
                isLiveDeskPaymentPending(payment, now)
            )
          : [];
        // Folding an empty list hands back `refreshed`, which is `state.registry` itself when
        // nothing moved — Immer's set trap no-ops that assignment, so an unchanged registry
        // keeps its identity and the refresher host does not re-render on every read.
        state.registry = toAdopt.reduce(
          (registry, payment) =>
            addToDeskPaymentRegistry(registry, payment, now),
          refreshed
        );
      },
      prepare: (seen: (DeskPayment | null | undefined)[], adopt = false) => ({
        payload: { seen, adopt, now: Date.now() }
      })
    },
    followDeskPayment: (state, { payload }: PayloadAction<ID>) => {
      state.followedPaymentIntentCounts[payload] =
        (state.followedPaymentIntentCounts[payload] ?? 0) + 1;
    },
    unfollowDeskPayment: (state, { payload }: PayloadAction<ID>) => {
      const next = (state.followedPaymentIntentCounts[payload] ?? 0) - 1;
      if (next > 0) {
        state.followedPaymentIntentCounts[payload] = next;
      } else {
        delete state.followedPaymentIntentCounts[payload];
      }
    }
  }
});

export const {
  registerDeskPayment,
  reconcileDeskPayments,
  followDeskPayment,
  unfollowDeskPayment
} = deskPaymentRegistrySlice.actions;

export const deskPaymentRegistryReducer = deskPaymentRegistrySlice.reducer;
